import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

type Consultor = Database['public']['Tables']['consultores']['Row'];
type Profile = Database['public']['Tables']['profiles']['Row'];

// Mapeamento de roles para tipos de consultor
const ROLE_TO_TIPO_MAP = {
  'ASSOCIADO': 'master',
  'LIDER_VENDAS': 'lider',
  'RELACIONADOR': 'relacionador',
  'SUPERVISOR': 'supervisor',
  'SUPER_ADMIN': 'master'
} as const;

type ConsultorRole = keyof typeof ROLE_TO_TIPO_MAP;

export class ConsultoresSupabaseService {
  /**
   * Cria um consultor baseado em um profile aprovado
   */
  static async criarConsultorDeProfile(
    profile: Profile,
    supervisorId?: number
  ): Promise<{ success: boolean; message: string; consultorId?: number }> {
    try {
      // Verificar se o role do profile é válido para criar consultor
      if (!(profile.role in ROLE_TO_TIPO_MAP)) {
        return {
          success: false,
          message: `Role ${profile.role} não é válido para criar consultor`
        };
      }

      const role = profile.role as ConsultorRole;
      const tipo = ROLE_TO_TIPO_MAP[role];

      // Buscar o supervisor se fornecido
      let supervisorConsultorId: number | null = null;
      if (supervisorId && (role === 'LIDER_VENDAS' || role === 'RELACIONADOR')) {
        const { data: supervisor, error: supervisorError } = await supabase
          .from('consultores')
          .select('id')
          .eq('id', supervisorId.toString())
          .eq('tenant_id', profile.tenant_id!)
          .single();

        if (supervisorError || !supervisor) {
          return {
            success: false,
            message: 'Supervisor não encontrado'
          };
        }

        supervisorConsultorId = Number(supervisor.id);
      }

      // Criar o consultor
      const { data: consultor, error: consultorError } = await supabase
        .from('consultores')
        .insert({
          user_id: profile.id, // Vincula o consultor ao profile do usuário
          nome: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
          email: '', // Email será preenchido posteriormente se necessário
          tenant_id: Number(profile.tenant_id!),
          tipo: tipo as any,
          supervisor_id: supervisorConsultorId,
          status_cadastro: 'COMPLETO',
          status: 'ativo'
        } as any)
        .select('id')
        .single();

      if (consultorError) {
        console.error('Erro ao criar consultor:', consultorError);
        return {
          success: false,
          message: `Erro ao criar consultor: ${consultorError.message}`
        };
      }

      return {
        success: true,
        message: 'Consultor criado com sucesso',
        consultorId: Number(consultor.id)
      };
    } catch (error) {
      console.error('Erro ao criar consultor de profile:', error);
      return {
        success: false,
        message: `Erro inesperado: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
      };
    }
  }

  /**
   * Busca consultores por tenant
   */
  static async listarConsultoresPorTenant(tenantId: number): Promise<Consultor[]> {
    try {
      const { data, error } = await supabase
        .from('consultores')
        .select('*')
        .eq('tenant_id', tenantId.toString())
        .eq('status', 'ativo')
        .order('nome');

      if (error) {
        console.error('Erro ao buscar consultores:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Erro no serviço de consultores:', error);
      throw error;
    }
  }

  /**
   * Busca supervisores por tenant (apenas consultores do tipo supervisor)
   */
  static async listarSupervisoresPorTenant(tenantId: number): Promise<Consultor[]> {
    try {
      const { data, error } = await supabase
        .from('consultores')
        .select('*')
        .eq('tenant_id', tenantId.toString())
        .eq('tipo', 'supervisor')
        .eq('status', 'ativo')
        .order('nome');

      if (error) {
        console.error('Erro ao buscar supervisores:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Erro no serviço de supervisores:', error);
      throw error;
    }
  }

  /**
   * Verifica se um profile já tem um consultor associado
   */
  static async consultorExisteParaProfile(profileId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('consultores')
        .select('id')
        .eq('user_id', profileId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Erro ao verificar consultor existente:', error);
        throw error;
      }

      return !!data;
    } catch (error) {
      console.error('Erro ao verificar consultor existente:', error);
      throw error;
    }
  }

  /**
   * Busca o consultor associado a um profile
   */
  static async buscarConsultorPorProfile(profileId: string): Promise<Consultor | null> {
    try {
      const { data, error } = await supabase
        .from('consultores')
        .select('*')
        .eq('user_id', profileId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Erro ao buscar consultor:', error);
        throw error;
      }

      return data || null;
    } catch (error) {
      console.error('Erro no serviço de busca de consultor:', error);
      throw error;
    }
  }
}